import React, { useState, useEffect } from "react";
import Modules from "../Components/Modules";
import {
  Modal,
  Button,
  Form,
  Dropdown,
  DropdownButton,
  Badge,
} from "react-bootstrap";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { FaPlus, FaStar, FaLayerGroup } from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";
import NormalModule from "../Components/NormalModule";
import "../App.css";
import "../Styles/ModulePage.css";

function ModulePage() {
  const [showModal, setShowModal] = useState(false);
  const [selectedValue, setSelectedValue] = useState("Select Status");
  const [saving, setSaving] = useState(false);
  const [moduleData, setModuleData] = useState({
    module_name: "",
    description: "",
    course: "",
    status: "",
  });
  const [userRole, setUserRole] = useState("learner");
  const isFormComplete = Boolean(
    moduleData.module_name.trim() &&
      moduleData.description.trim() &&
      moduleData.course &&
      moduleData.status,
  );

  useEffect(() => {
    const loggedInUser = JSON.parse(localStorage.getItem("user"));

    if (loggedInUser && loggedInUser.user_role) {
      setUserRole(loggedInUser.user_role);
    }
  }, []);

  const handleOpenModal = () => setShowModal(true);

  const handleCloseModal = () => {
    setShowModal(false);
    setModuleData({
      module_name: "",
      description: "",
      course: "",
      status: "",
    });
    setSelectedValue("Select Status");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setModuleData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSelect = (eventKey) => {
    setSelectedValue(eventKey);
    setModuleData((prevData) => ({ ...prevData, status: eventKey }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !moduleData.module_name ||
      !moduleData.description ||
      !moduleData.course ||
      !moduleData.status
    ) {
      toast.error("Please fill all required fields.", {
        position: "top-right",
        autoClose: 2000,
      });
      return;
    }

    setSaving(true);

    const loginUser = JSON.parse(localStorage.getItem("user"));

    const payload = {
      ...moduleData,
      action_user_id: loginUser?.user_id || null,
      action_username: loginUser?.username || "Unknown",
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}/insertModule`,
        payload,
      );

      if (response.data.success) {
        toast.success(response.data.message || "Module created successfully!", {
          position: "top-right",
          autoClose: 1200,
        });

        handleCloseModal();

        setTimeout(() => {
          window.location.reload();
        }, 1200);
      } else {
        toast.error(response.data.message || "Failed to create module.", {
          position: "top-right",
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Error saving module:", error);

      toast.error(error.response?.data?.message || "Server Error!", {
        position: "top-right",
        autoClose: 2000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="modules-page" aria-labelledby="modules-page-title">
      <header className="modules-page__header">
        <div className="modules-page__heading">
          <div>
            <p className="modules-page__eyebrow">LEARNING CONTENT</p>
            <h1 id="modules-page-title">Modules</h1>
            <p>Manage learning modules, course groups, and assessment access.</p>
          </div>
        </div>

        {userRole === "admin" && (
          <Button onClick={handleOpenModal} className="modules-page__add-button">
            <FaPlus size={13} aria-hidden="true" /> Add Module
          </Button>
        )}
      </header>

      <section className="modules-page__section" aria-labelledby="starred-modules-title">
        <div className="modules-page__section-header">
          <div className="modules-page__section-title">
            <FaStar aria-hidden="true" />
            <div>
              <h2 id="starred-modules-title">Starred modules</h2>
              <p>Your featured and frequently accessed content.</p>
            </div>
          </div>

          <Badge className="modules-page__featured-badge">
            Featured
          </Badge>
        </div>

        <Modules />
      </section>

      <section className="modules-page__section" aria-labelledby="all-modules-title">
        <div className="modules-page__section-header">
          <div className="modules-page__section-title modules-page__section-title--all">
            <FaLayerGroup aria-hidden="true" />
            <div>
              <h2 id="all-modules-title">All modules</h2>
              <p>Browse all available learning and assessment content.</p>
            </div>
          </div>
        </div>

        <NormalModule />
      </section>

      {userRole === "admin" && (
        <Modal
          show={showModal}
          onHide={handleCloseModal}
          size="lg"
          centered
          className="module-create-modal"
          aria-labelledby="create-module-title"
        >
          <form onSubmit={handleSubmit}>
            <Modal.Header closeButton className="module-create-modal__header">
              <div>
                <p className="module-create-modal__eyebrow">NEW LEARNING CONTENT</p>
                <Modal.Title id="create-module-title">Create module</Modal.Title>
                <p className="module-create-modal__intro">
                  Add the module details now. Assessments can be created after saving.
                </p>
              </div>
            </Modal.Header>

            <Modal.Body className="module-create-modal__body">
              <div className="module-create-modal__field">
                <Form.Label htmlFor="module-name">Module name <span>*</span></Form.Label>
                <Form.Control
                  id="module-name"
                  type="text"
                  placeholder="Enter module name"
                  name="module_name"
                  value={moduleData.module_name}
                  onChange={handleInputChange}
                  maxLength={150}
                  required
                  autoFocus
                />
                <Form.Text>Use a short, recognisable title for learners.</Form.Text>
              </div>

              <div className="module-create-modal__field">
                <Form.Label htmlFor="module-description">Description <span>*</span></Form.Label>
                <Form.Control
                  id="module-description"
                  as="textarea"
                  rows={3}
                  placeholder="Write a short description"
                  name="description"
                  value={moduleData.description}
                  onChange={handleInputChange}
                  maxLength={300}
                  required
                />
                <div className="module-create-modal__field-footer">
                  <Form.Text>Explain what learners will find in this module.</Form.Text>
                  <span>{moduleData.description.length}/300</span>
                </div>
              </div>

              <div className="module-create-modal__grid">
                <div className="module-create-modal__field">
                  <Form.Label>Course <span>*</span></Form.Label>
                  <DropdownButton
                    title={moduleData.course || "Select Course"}
                    onSelect={(selectedKey) =>
                      handleInputChange({
                        target: { name: "course", value: selectedKey },
                      })
                    }
                    variant="outline-secondary"
                    className="w-100 module-dropdown module-create-modal__dropdown"
                  >
                    <Dropdown.Item eventKey="English">English</Dropdown.Item>
                    <Dropdown.Item eventKey="Chinese">Chinese</Dropdown.Item>
                  </DropdownButton>
                </div>

                <div className="module-create-modal__field">
                  <Form.Label>Status <span>*</span></Form.Label>
                  <DropdownButton
                    title={selectedValue}
                    onSelect={handleSelect}
                    variant="outline-secondary"
                    className="w-100 module-dropdown module-create-modal__dropdown"
                  >
                    <Dropdown.Item eventKey="Normal">Normal</Dropdown.Item>
                    <Dropdown.Item eventKey="Starred">Starred</Dropdown.Item>
                  </DropdownButton>
                </div>
              </div>
            </Modal.Body>

            <Modal.Footer className="module-create-modal__footer">
             
              <Button
                variant="light"
                onClick={handleCloseModal}
                disabled={saving}
                className="module-create-modal__cancel"
              >
                Cancel
              </Button>

              <Button
                type="submit"
                disabled={saving || !isFormComplete}
                className="module-create-modal__save"
              >
                {saving ? "Saving..." : "Save Module"}
              </Button>
            </Modal.Footer>
          </form>
        </Modal>
      )}

      <ToastContainer />
    </section>
  );
}

export default ModulePage;
